/**
 * Marks the matched words in the bubbles of a search jump (SRE-3328).
 *
 * Through the CSS Custom Highlight API (`CSS.highlights`, styled by
 * `::highlight(chat-search)` and `::highlight(chat-search-active)` in
 * MessageContainer.styles.scss): the bubbles render sanitized HTML through
 * `dangerouslySetInnerHTML`, and marking words through React state would mean
 * rewriting that HTML — a new injection surface — and re-rendering every bubble
 * for a tint. A highlight range changes no node at all.
 *
 * Two highlights, one look (the `--search-match-*` aliases in main.scss):
 *   chat-search        — every match of an in-chat search, in yellow;
 *   chat-search-active — the words inside the ONE bubble the user is on, in
 *                        yellow and underlined. It is the "you are here"
 *                        signal for the in-chat search's current match and
 *                        for a jump from an inbox result, and it replaced the
 *                        purple ring.
 *
 * Where the API is missing (older browsers, jsdom) the marks are simply not
 * drawn and the jumped bubble gets a neutral fallback tint instead (see
 * MessageContainer.styles.scss). The environment is injectable so the logic is
 * testable anyway.
 */

export const CHAT_SEARCH_HIGHLIGHT_NAME = 'chat-search';
export const CHAT_SEARCH_ACTIVE_HIGHLIGHT_NAME = 'chat-search-active';

/**
 * The active mark paints over the all-matches one where both cover a word —
 * an inbox jump into a thread whose in-chat search is also open. Registration
 * order would otherwise decide.
 */
const ACTIVE_HIGHLIGHT_PRIORITY = 1;

export type tHighlightEnvironment = {
  registry: {
    set: (name: string, highlight: unknown) => unknown;
    delete: (name: string) => unknown;
    /** Present on the platform registry (it is maplike). */
    get?: (name: string) => unknown;
  };
  createHighlight: (ranges: Range[], priority?: number) => unknown;
};

type tHighlightGlobals = {
  CSS?: { highlights?: tHighlightEnvironment['registry'] };
  Highlight?: new (...ranges: Range[]) => { priority?: number };
};

/** The platform's highlight registry, or null where it does not exist. */
export function getHighlightEnvironment(): tHighlightEnvironment | null {
  const globals = globalThis as unknown as tHighlightGlobals;
  const registry = globals.CSS?.highlights;
  const HighlightConstructor = globals.Highlight;

  if (!registry || typeof HighlightConstructor !== 'function') return null;

  return {
    registry,
    createHighlight: (ranges, priority) => {
      const highlight = new HighlightConstructor(...ranges);

      if (priority !== undefined) highlight.priority = priority;

      return highlight;
    },
  };
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * One case-insensitive pattern for every term, bound to whole words so
 * "envío" does not light up inside "reenvío".
 */
function buildTermPattern(terms: string[]): RegExp | null {
  const alternatives = terms.filter(Boolean).map(escapeRegExp);

  if (!alternatives.length) return null;

  return new RegExp(
    `(?<![\\p{L}\\p{N}])(?:${alternatives.join('|')})(?![\\p{L}\\p{N}])`,
    'giu',
  );
}

/**
 * The ranges of every occurrence of `terms` in the text under `root`.
 *
 * Text nodes are matched one at a time, so a word split across two nodes (a
 * link in the middle of it) is not marked — the cost of never touching the DOM.
 *
 * @param root - A bubble node.
 * @param terms - The words to mark, as the server marked them.
 * @returns One range per occurrence, in document order.
 */
export function collectTermRanges(root: Node, terms: string[]): Range[] {
  const pattern = buildTermPattern(terms);
  const doc = root.ownerDocument;

  if (!pattern || !doc) return [];

  const ranges: Range[] = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.nodeValue ?? '';

    for (const match of text.matchAll(pattern)) {
      if (!match[0] || match.index === undefined) continue;

      const range = doc.createRange();
      range.setStart(node, match.index);
      range.setEnd(node, match.index + match[0].length);
      ranges.push(range);
    }
  }

  return ranges;
}

const resolveEnvironment = (environment?: tHighlightEnvironment | null) =>
  environment === undefined ? getHighlightEnvironment() : environment;

/**
 * Registers `ranges` under `name` and returns the function that removes them.
 *
 * No ranges means nothing of this name should be on screen, so a stale one is
 * deleted. The cleanup deletes only its OWN highlight: the active name has two
 * writers (the in-chat search and an inbox jump), and the one that finishes
 * last must not wipe the mark the other has drawn since.
 */
function drawHighlight(params: {
  environment: tHighlightEnvironment;
  highlight: { name: string; ranges: Range[]; priority?: number };
}): () => void {
  const { environment } = params;
  const { name, ranges, priority } = params.highlight;
  const { registry } = environment;

  if (!ranges.length) {
    registry.delete(name);

    return () => {};
  }

  const highlight = environment.createHighlight(ranges, priority);

  registry.set(name, highlight);

  return () => {
    if (registry.get && registry.get(name) !== highlight) return;

    registry.delete(name);
  };
}

/**
 * Draws the in-chat search marks and returns the function that removes them.
 *
 * Every match's words go into `chat-search`, except the active bubble's, which
 * go into `chat-search-active` alone — so the active words have one colour,
 * never two stacked.
 *
 * @param params.nodes - The bubbles of the loaded matches.
 * @param params.activeNode - The bubble of the match the search is on, if
 *   loaded.
 * @param params.terms - The words to mark.
 * @param params.environment - The highlight registry; defaults to the
 *   platform's. Null means "not supported": nothing is drawn.
 * @returns A cleanup that removes both marks.
 */
export function applyChatSearchHighlight(params: {
  nodes: Node[];
  activeNode?: Node | null;
  terms: string[];
  environment?: tHighlightEnvironment | null;
}): () => void {
  const { nodes, activeNode, terms } = params;
  const environment = resolveEnvironment(params.environment);

  if (!environment) return () => {};

  const clearAll = drawHighlight({
    environment,
    highlight: {
      name: CHAT_SEARCH_HIGHLIGHT_NAME,
      ranges: nodes
        .filter((node) => node !== activeNode)
        .flatMap((node) => collectTermRanges(node, terms)),
    },
  });
  const clearActive = drawHighlight({
    environment,
    highlight: {
      name: CHAT_SEARCH_ACTIVE_HIGHLIGHT_NAME,
      ranges: activeNode ? collectTermRanges(activeNode, terms) : [],
      priority: ACTIVE_HIGHLIGHT_PRIORITY,
    },
  });

  return () => {
    clearAll();
    clearActive();
  };
}

/**
 * Draws only the active mark, over one bubble — what a jump from an inbox
 * result shows on the message it opened. Leaves `chat-search` alone.
 *
 * @param params.node - The jumped-to bubble.
 * @param params.terms - The words the result's snippet marked.
 * @param params.environment - As in applyChatSearchHighlight.
 * @returns A cleanup that removes the mark, if it is still this one.
 */
export function applyActiveMatchHighlight(params: {
  node: Node;
  terms: string[];
  environment?: tHighlightEnvironment | null;
}): () => void {
  const environment = resolveEnvironment(params.environment);

  if (!environment) return () => {};

  const ranges = collectTermRanges(params.node, params.terms);

  // Nothing to mark is not "clear the active mark": that one may be the in-chat
  // search's, and it is not this jump's to remove.
  if (!ranges.length) return () => {};

  return drawHighlight({
    environment,
    highlight: {
      name: CHAT_SEARCH_ACTIVE_HIGHLIGHT_NAME,
      ranges,
      priority: ACTIVE_HIGHLIGHT_PRIORITY,
    },
  });
}
